import { useCallback, useEffect, useRef, useState } from 'react'
import { Media } from '@/components/Media'
import { PlayBadge } from '@/components/PlayBadge'
import type { VfxContent } from '@/lib/content.types'

export function Vfx({ data }: { data: VfxContent }) {
  const clips = data.clips
  const count = clips.length
  const [active, setActive] = useState(0)
  const [paused, setPaused] = useState(false)
  /** 현재 클립이 영상이고, 실제로 재생 중인지 */
  const [videoPlaying, setVideoPlaying] = useState(false)
  const stageRef = useRef<HTMLDivElement>(null)

  const go = useCallback(
    (delta: number) => setActive((i) => (i + delta + count) % count),
    [count],
  )

  // 클립이 바뀌면 재생 상태를 초기화한다 (새 영상이 재생을 시작하면 다시 true가 된다)
  useEffect(() => setVideoPlaying(false), [active])

  /**
   * Admin에서 클립을 지워 개수가 줄면 active 가 범위를 벗어날 수 있다.
   * 그대로 두면 아무 클립도 안 보이고 카운터만 "04 / 03" 처럼 어긋난다.
   */
  useEffect(() => {
    setActive((i) => Math.min(i, Math.max(0, count - 1)))
  }, [count])

  /**
   * 영상이 재생 중이면 타이머를 쓰지 않고 **영상이 끝날 때** 다음 클립으로 넘어간다.
   * 이미지 클립이거나, 영상이 재생되지 않는 경우(자동 재생 차단·로드 실패)에는
   * 기존처럼 autoRotateMs 타이머로 넘어간다 — 캐러셀이 멈춰버리지 않도록 하는 안전장치다.
   */
  const useTimer = !videoPlaying

  useEffect(() => {
    if (!useTimer) return
    if (!data.autoRotateMs || paused || count < 2) return
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReduced) return
    const id = window.setInterval(() => setActive((i) => (i + 1) % count), data.autoRotateMs)
    return () => window.clearInterval(id)
  }, [data.autoRotateMs, paused, count, active, useTimer])

  const pad = (n: number) => String(n).padStart(2, '0')

  return (
    // Studio가 alt 밴드를 맡으므로 VFX는 base 밴드로 둔다 (밝기 교차 유지)
    <section id="vfx" className="relative px-5 py-20 md:px-10 md:py-[104px]">
      {/* 모바일에서는 전체 중앙 정렬, md 이상에서는 디자인대로 좌측 정렬 */}
      <div className="mx-auto max-w-narrow text-center md:text-left">
        <div className="mb-8 flex flex-col items-center gap-5 md:flex-row md:flex-wrap md:items-end md:justify-between">
          <div>
            <div className="annx-eyebrow mb-3.5">{data.eyebrow}</div>
            <Media
              media={data.logo}
              className="annx-section-logo mb-3.5"
            />
            <p className="annx-deck mx-auto max-w-[560px] md:mx-0">{data.deck}</p>
          </div>
          <a href={data.learnMore.href} className="annx-learn-more">
            {data.learnMore.label}
          </a>
        </div>

        <div
          ref={stageRef}
          className="annx-media-frame aspect-video"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
        >
          {clips.map((clip, i) => (
            <div
              key={clip.id}
              className="absolute inset-0 transition-opacity duration-[550ms] ease-out"
              style={{ opacity: i === active ? 1 : 0, pointerEvents: i === active ? 'auto' : 'none' }}
              aria-hidden={i !== active}
            >
              {/* active=false 인 클립은 재생하지 않고 파일도 받지 않는다.
                  활성 클립은 loop를 끄고 끝나는 순간 다음으로 넘어간다.
                  단 마우스를 올려둔 동안(paused)에는 loop를 켜서 넘어가지 않고 계속 돈다. */}
              <Media
                media={clip.media}
                active={i === active}
                loop={i === active ? paused : true}
                onEnded={i === active ? () => go(1) : undefined}
                onPlayingChange={i === active ? setVideoPlaying : undefined}
              />
            </div>
          ))}
          {/* 포스터 이미지일 때만 재생 배지를 얹는다 */}
          {clips[active]?.media.kind === 'image' && <PlayBadge size={62} />}
        </div>

        {/* 컨트롤은 항상 영상 아래 — 미디어를 가리지 않는다.
            모바일에서는 세로로 쌓아 중앙 정렬한다. */}
        <div className="mt-5 flex flex-col items-center gap-4 md:flex-row md:justify-between md:gap-0">
          <div className="flex items-center gap-[9px]">
            {clips.map((clip, i) => (
              <button
                key={clip.id}
                type="button"
                aria-label={`Show clip ${i + 1}`}
                onClick={() => {
                  setActive(i)
                }}
                className="h-[4px] rounded-[3px] transition-all duration-300"
                style={{
                  width: i === active ? 30 : 9,
                  background: i === active ? '#6AE7FA' : '#262A33',
                }}
              />
            ))}
            <span
              aria-live="polite"
              className="ml-2 font-mono text-xs font-medium text-ink-tertiary"
            >
              {pad(active + 1)} / {pad(count)}
            </span>
          </div>

          <div className="flex gap-[9px]">
            <button
              type="button"
              aria-label="Previous clip"
              onClick={() => go(-1)}
              className="flex h-[34px] w-[34px] items-center justify-center rounded-full border border-line-button bg-transparent transition-colors hover:border-blue-brand"
            >
              <span
                aria-hidden="true"
                className="mr-0.5"
                style={{
                  borderRight: '6px solid #A2A8B4',
                  borderTop: '5px solid transparent',
                  borderBottom: '5px solid transparent',
                }}
              />
            </button>
            <button
              type="button"
              aria-label="Next clip"
              onClick={() => go(1)}
              className="flex h-[34px] w-[34px] items-center justify-center rounded-full border border-line-button bg-transparent transition-colors hover:border-blue-brand"
            >
              <span
                aria-hidden="true"
                className="ml-0.5"
                style={{
                  borderLeft: '6px solid #F2F4F7',
                  borderTop: '5px solid transparent',
                  borderBottom: '5px solid transparent',
                }}
              />
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}
